import * as shared from '@aloft/toolkit-shared';
import { expected } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router';

import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary.tsx';
import { GuidedTours } from '../../components/GuidedTour/GuidedTours.tsx';
import { Tkv2ReferenceBar } from '../../components/ReferenceBar/Tkv2ReferenceBar.tsx';
import { ToolkitGenericFallback } from '../../components/ToolkitGenericFallback.tsx';
import { MainLayout } from '../../layout/MainLayout.tsx';
import { logger } from '../../logging/logging.ts';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { getReferenceProperty } from '../../modules/utils/getReferenceProperty.ts';
import {
  setProperties,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectCompSequence, selectIsInspectifyOrder, selectStartedAt } from '../../redux/features/selectors/orderSelectors.ts';
import { selectSubjectAddress } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { Tkv2ControllerReferenceBarContext } from '../Tkv2ControllerReferenceBarContext.ts';
import { CraftsmanOrderSyncDriver } from '../craftsmanOrderSync/CraftsmanOrderSyncDriver.tsx';
import { useNewTkv2ReferenceBarUserInterface } from '../hooks/useNewTkv2ReferenceBarUserInterface.ts';
import { startOrder } from '../../redux/features/orderSlice.ts';
import { useAppraisifyStopAndCall } from '../../hooks/validation/useAppraisifyStopAndCall.tsx';

const log = logger();

export const CraftsmanOrderRootElement = () => {
  const { orderId } = useParams();
  const referenceBarUI = useNewTkv2ReferenceBarUserInterface();

  (window as any).shared = shared;

  return (
    <CraftsmanOrderSyncDriver orderID={expected(orderId)}>
      <Tkv2ControllerReferenceBarContext.Provider value={referenceBarUI}>
        <Root />
      </Tkv2ControllerReferenceBarContext.Provider>
    </CraftsmanOrderSyncDriver>
  );
};

const Root = () => {
  const subjectAddress = useAppSelector(selectSubjectAddress);
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);
  const startedAt = useAppSelector(selectStartedAt);
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const { stopAndCallModal } = useAppraisifyStopAndCall();

  useEffect(() => {
    // Create property objects for all comparables
    const comparableProperties = compSequence.map((compId) =>
      getReferenceProperty(compId, compSequence),
    );

    // Set the properties array to show all comparables in the Reference Bar
    dispatch(setProperties(comparableProperties));
  }, [dispatch, compSequence]);

  useEffect(() => {
    if (!startedAt && isInspectifyOrder) {
      dispatch(startOrder());
    }
  }, [startedAt]);

  return (
    <MainLayout title={subjectAddress}>
      <Flex height="100%" width="100%">
        <ErrorBoundary
          Fallback={ToolkitGenericFallback}
          onError={() => log.fatal('ToolkitControllerOrderGlobal failure')}
        >
          <Tkv2ReferenceViewDrawer>
            <Outlet />
          </Tkv2ReferenceViewDrawer>
          <Tkv2ReferenceBar />
          <GuidedTours />
          {stopAndCallModal()}
        </ErrorBoundary>
      </Flex>
    </MainLayout>
  );
};
